import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import VehicleSelector from './VehicleSelector';
import { driverSiteUrl, isPartnerSite } from '../utils/siteHost';

const MenuIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
        <line x1="9" y1="3" x2="9" y2="21" />
    </svg>
);

const ChatIcon = ({ active }) => (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={active ? '2.6' : '2.2'} strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
    </svg>
);

const GarageIcon = ({ active }) => (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={active ? '2.6' : '2.2'} strokeLinecap="round" strokeLinejoin="round">
        <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2" />
        <circle cx="7" cy="17" r="2" />
        <path d="M9 17h6" />
        <circle cx="17" cy="17" r="2" />
    </svg>
);

const GloveboxIcon = ({ active }) => (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={active ? '2.6' : '2.2'} strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
    </svg>
);

const BellIcon = ({ active }) => (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={active ? '2.6' : '2.2'} strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.7 21a2 2 0 0 1-3.4 0" />
    </svg>
);

const SettingsIcon = ({ active }) => (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={active ? '2.6' : '2.2'} strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06A1.7 1.7 0 0 0 15 19.4a1.7 1.7 0 0 0-1 .6 1.7 1.7 0 0 0-.4 1.1V21a2 2 0 1 1-4 0v-.09A1.7 1.7 0 0 0 8.6 19.4a1.7 1.7 0 0 0-1.88.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-.6-1 1.7 1.7 0 0 0-1.1-.4H3a2 2 0 1 1 0-4h.09A1.7 1.7 0 0 0 4.6 8.6a1.7 1.7 0 0 0-.34-1.88l-.06-.06A2 2 0 1 1 7.03 3.83l.06.06A1.7 1.7 0 0 0 9 4.6a1.7 1.7 0 0 0 1-.6 1.7 1.7 0 0 0 .4-1.1V3a2 2 0 1 1 4 0v.09A1.7 1.7 0 0 0 15.4 4.6a1.7 1.7 0 0 0 1.88-.34l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.7 1.7 0 0 0 19.4 9c.4.2.7.5 1 .9.3.3.7.4 1.1.4H21a2 2 0 1 1 0 4h-.09a1.7 1.7 0 0 0-1.51.7z" />
    </svg>
);

const navItems = [
    { label: 'Chat', path: '/chat', match: ['/chat', '/test-chat'], Icon: ChatIcon },
    { label: 'Garage', path: '/garage', match: ['/garage'], Icon: GarageIcon },
    { label: 'Glove Box', path: '/glovebox', match: ['/glovebox'], Icon: GloveboxIcon },
    { label: 'Notifications', path: '/notifications', match: ['/notifications'], Icon: BellIcon, badgeKey: 'notifications' },
    { label: 'Settings', path: '/settings', match: ['/settings'], Icon: SettingsIcon },
];

const isActivePath = (pathname, matches) => (
    matches.some((match) => pathname === match || pathname.startsWith(`${match}/`))
);

// isAdminView is driven by the route, not by the admin cookie: on /shop the
// mobile chrome belongs to the shop, so the driver's vehicle picker and tab bar
// stay out of it. The shop page carries its own "Back to chat" link.
const MobileAppNav = ({ authState, onMenuClick, isAdminView = false, notificationsUnread = 0 }) => {
    const location = useLocation();
    const navigate = useNavigate();

    return (
        <>
            <header className="md:hidden fixed inset-x-0 top-0 z-[54] border-b border-[#d9d9d9]/90 bg-[#F9F9F9]/92 shadow-[0_4px_18px_rgba(45,45,45,0.06)] backdrop-blur-xl">
                <div className="flex h-14 items-center gap-2 px-2.5">
                    <button
                        type="button"
                        onClick={onMenuClick}
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-[#d9d9d9] bg-white/70 text-[#414141] shadow-sm transition hover:bg-white/90 active:scale-95"
                        aria-label="Open menu"
                    >
                        <MenuIcon />
                    </button>

                    {/* Partner portal: a real link to the driver site in a new tab,
                        matching the sidebar logo, so the shop keeps its dashboard. */}
                    {isPartnerSite() ? (
                        <a
                            href={driverSiteUrl()}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex h-9 w-9 shrink-0 items-center justify-center text-left"
                            aria-label="Open CarFido in a new tab"
                            title="Open CarFido in a new tab"
                        >
                            <img src="/PNG/carfido-icon-transparent.png" alt="" className="h-8 w-8 shrink-0 object-contain" />
                        </a>
                    ) : (
                        <button
                            type="button"
                            onClick={() => navigate(isAdminView ? '/shop' : '/chat')}
                            className="flex h-9 w-9 shrink-0 items-center justify-center text-left"
                            aria-label={isAdminView ? 'Go to shop admin' : 'Go to chat'}
                        >
                            <img src="/PNG/carfido-icon-transparent.png" alt="" className="h-8 w-8 shrink-0 object-contain" />
                        </button>
                    )}

                    {isAdminView ? (
                        <span className="ml-auto inline-flex h-9 shrink-0 items-center rounded-xl border border-[#d9d9d9] bg-white/70 px-3 text-[11px] font-black uppercase tracking-[0.12em] text-[#414141] shadow-sm">
                            Shop Admin
                        </span>
                    ) : (
                        <div className="ml-auto min-w-0 flex-1 max-w-[230px]">
                            <VehicleSelector authState={authState} variant="mobileTop" onNavigate={navigate} />
                        </div>
                    )}
                </div>
            </header>

            {!isAdminView && (
                <nav
                    className="md:hidden fixed inset-x-0 bottom-0 z-[54] border-t border-[#d9d9d9]/90 bg-[#F9F9F9]/92 shadow-[0_-8px_24px_rgba(45,45,45,0.08)] backdrop-blur-xl"
                    style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
                    aria-label="Primary"
                >
                    <div className="grid h-14 grid-cols-5 px-1.5">
                        {navItems.map(({ label, path, match, Icon, badgeKey }) => {
                            const active = isActivePath(location.pathname, match);
                            const badge = badgeKey === 'notifications' ? notificationsUnread : 0;
                            return (
                                <button
                                    key={path}
                                    type="button"
                                    onClick={() => navigate(path)}
                                    className={`flex min-w-0 flex-col items-center justify-center gap-0.5 rounded-2xl text-[9px] font-black transition ${
                                        active ? 'text-[#D81F26]' : 'text-[#414141] hover:text-[#414141] active:text-[#414141]'
                                    }`}
                                    aria-current={active ? 'page' : undefined}
                                >
                                    <span className={`relative flex h-6 w-8 items-center justify-center rounded-full transition ${
                                        active ? 'bg-[#f0f0f0] shadow-[inset_0_0_0_1px_rgba(216,31,38,0.08)]' : 'bg-transparent'
                                    }`}>
                                        <Icon active={active} />
                                        {badge > 0 && (
                                            <span className="absolute -top-1 right-0 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-[#e02424] px-1 text-[8px] font-black leading-none text-white ring-2 ring-[#F9F9F9]">
                                                {badge > 9 ? '9+' : badge}
                                            </span>
                                        )}
                                    </span>
                                    <span className="max-w-full truncate px-0.5 leading-none tracking-tight" title={label}>{label}</span>
                                </button>
                            );
                        })}
                    </div>
                </nav>
            )}
        </>
    );
};

export default MobileAppNav;
